import {CommentStatus} from '@domain/comments'
import {
  ActionSource,
  UserDto,
  AnswerStatus,
  QuestionDifficulty,
  DifficultySummaries,
  ActionStatusType,
  KpiName,
  MipElement
} from '@domain/pom'
import {Approver} from '@domain/shared'
import {Permission, RoleType} from '@hconnect/apiclient'
import {Attachment} from '@hconnect/common/types'
import {DateRange} from '@hconnect/uikit'
import type {SortDirection} from '@mui/material'

import {LocationDtoType, ActionItemMutation} from './backend.types'

export interface WithId {
  id: string
}

export interface WithName {
  name: string
}

export interface WithScoring {
  score: number
  completion: number
  difficultySummaries: DifficultySummaries
}

export interface WithStats extends WithId, WithName, WithScoring {
  responsible?: UserDto
}

export interface Topic extends WithStats {
  sectionId: string
  guidanceLink?: string
  questions?: Question[]
}

export interface Question extends WithId {
  difficulty: QuestionDifficulty
  submission?: {
    answer: AnswerStatus
  }
}

export interface Section extends WithStats {
  topics: Topic[]
  guidanceLink?: string
}

export type ParentSection = Pick<Section, 'id' | 'name'>

export type ParentTopic = Pick<Topic, 'id' | 'name'>

export interface ActionItem extends WithId {
  plantUpmId: string
  questionId: string
  title: string
  concurrencyStamp: string // contains a UUID
  description: string
  assignee?: UserDto
  creator?: UserDto
  updatedBy?: UserDto
  createdAt: Date
  updatedAt: Date
  source: ActionSource
  status: ActionStatusType
  shifthandoverTaskId: string
  dueDate: Date
  attachments: Attachment[]
  topic?: ParentTopic
  section?: ParentSection
  approvers?: Approver[]
  notifyAt?: Date
  costLeadership?: boolean
  element?: MipElement
  kpiName?: KpiName
  targetCosts?: string
  actualCostsYtd?: string
  targetKpi?: string
  actualKpiYtd?: string
}

export type ActionItemKey = keyof (ActionItem & ParentTopic)

export interface Plant extends WithStats {
  sections: Section[]
  timezone: string
  guidanceLink: string
}

export interface PomUser {
  email: string
  userId: string
  name: string
  roles: RoleType[]
  permissions: Permission[]
}

export const LocationType = {
  ...LocationDtoType
} as const

export type LocationType = Uncapitalize<keyof typeof LocationType>

export interface LocationStats extends WithStats {
  countryCode?: string // if the type is country
  type: LocationType
  lastUpdate?: Date
  subLocations: LocationStats[]
}

export interface PlantLocationStats extends LocationStats {
  type: 'plant'
  plantCode: string
  countryCode?: undefined
  timezone?: string
  subLocations: never[]
}

export type CreateActionItem = Omit<
  ActionItemMutation,
  'assigneeId' | 'dueDate' | 'approvers' | 'notifyAt'
> & {
  assignee?: UserDto
  dueDate: Date
  approvers?: Approver[]
  notifyAt?: Date
}

export interface PomComment {
  id: string
  createdOn: Date
  updatedOn?: Date
  createdBy: string
  updatedBy?: string
  status: CommentStatus
  referenceId: string // what the comment refers to, e.g. a question ID
  value: string
}

export type ActionFilterParameters = {
  title?: string
  sources?: ActionSource[]
  costLeadership?: boolean
  statuses?: ActionStatusType[]
  questionCategories?: QuestionDifficulty[]
  sectionIds?: string[]
  topicIds?: string[]
  questionIds?: string[]
  assignees?: string[]
  dueDateRange?: DateRange<Date>
}

export interface FilteredActionsParams extends ActionFilterParameters {
  plantId: string
  page: number
  rowsPerPage: number
  sortByKey?: ActionItemKey
  order?: SortDirection
}

export interface PaginatedList<T> {
  list: T[]
  total: number
  page: number
}

export interface QuestionAnswer {
  plantId: string
  questionId: string
  answer: AnswerStatus
}
